/**
 * LiveInterviewChatPanel — Phase D, Part 3/6: in-call chat, shared by
 * RecruiterRoomPage (recruiter side) and LiveInterviewRoomPage (candidate
 * side). Deliberately auth-agnostic: the two room pages differ in how they
 * load history / mark messages seen (authenticated liveInterviewApi vs.
 * public, token-gated liveInterviewPublicApi), so those two operations are
 * injected as props (`loadHistory`/`markSeen`) rather than this component
 * picking an API module itself. Sending a message always goes out over the
 * shared signaling WebSocket (`onSend`, which the parent wires to
 * useLiveInterviewCall's `sendAppMessage({ type: 'chat', text })`) — this
 * component never posts a new message over REST.
 *
 * Message shape matches both liveInterviewApi.LiveInterviewChatMessage and
 * liveInterviewPublicApi.LiveInterviewChatMessage verbatim (same fields).
 *
 * "Own side" vs. "their side" is decided purely by `sender_role` (recruiter
 * vs candidate), not by resolving exactly which recruiter seat sent a given
 * message — with only one candidate ever in a session, this reads correctly
 * as a normal two-party chat even when more than one recruiter seat is
 * connected; distinguishing individual recruiter seats from each other is
 * out of scope for this pass.
 */
import React, { useEffect, useRef, useState } from 'react'
import { PaperAirplaneIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'

export interface LiveInterviewChatMessage {
  id: string
  participant_id: string
  sender_role?: string | null
  text: string
  sent_at?: string | null
  seen_by: string[]
}

interface LiveInterviewChatPanelProps {
  ownRole: 'recruiter' | 'candidate'
  messages: LiveInterviewChatMessage[]
  onSend: (text: string) => void
  markSeen: (messageId: string) => Promise<void>
  /** True while the other side has an in-progress, unsent draft. */
  otherTyping?: boolean
  /** Fired (debounced) as this side's own draft goes from empty/idle to
   * typing and back — the caller wires this to a `typing` signal over the
   * signaling socket. Omit to disable sending a typing signal at all. */
  onTypingChange?: (isTyping: boolean) => void
  className?: string
  /** Suppresses this panel's own "Chat" label — for a caller (e.g.
   * RecruiterRoomPage's split-pane workspace) that already renders its own
   * section header immediately above this component. Defaults to false so
   * every other caller's layout is unaffected. */
  hideHeader?: boolean
  /** Swaps the message list/input from this component's original light
   * theme to a dark one matching RecruiterRoomPage's dark workspace panes.
   * Defaults to false (light) so the candidate room's own styling is
   * unaffected. */
  dark?: boolean
  /** True when the caller places this component inside a container whose
   * OWN height is already fully determined by a flexible parent (e.g.
   * RecruiterRoomPage's Pane, itself sized by a SplitPane) — the message
   * list then grows to fill exactly that via `flex-1 min-h-0`, with no cap.
   * False (the default) is for a caller whose wrapping element has no
   * explicit height of its own (e.g. the candidate room's plain
   * `bg-white rounded-xl ...` card) — there, `flex-1` has nothing to grow
   * against, so the list instead keeps its ORIGINAL fixed
   * `min-h-[140px] max-h-64`, unchanged from before this component's
   * flex-structure fix. Getting this backwards either caps the recruiter's
   * roomy pane at a useless 16rem or collapses the candidate room's chat to
   * whatever height its last few messages happen to need. */
  fillHeight?: boolean
}

const TYPING_STOP_DELAY_MS = 2000

function formatTime(value?: string | null): string {
  if (!value) return ''
  try {
    return new Date(value).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

export default function LiveInterviewChatPanel({
  ownRole, messages, onSend, markSeen, otherTyping, onTypingChange, className, hideHeader, dark, fillHeight,
}: LiveInterviewChatPanelProps) {
  const [draft, setDraft] = useState('')
  const listRef = useRef<HTMLDivElement>(null)
  const markedRef = useRef<Set<string>>(new Set())
  const typingRef = useRef(false)
  const typingStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const stopTyping = () => {
    if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current)
    if (typingRef.current) {
      typingRef.current = false
      onTypingChange?.(false)
    }
  }

  const handleDraftChange = (value: string) => {
    setDraft(value)
    if (!onTypingChange) return
    if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current)
    if (value.trim()) {
      if (!typingRef.current) {
        typingRef.current = true
        onTypingChange(true)
      }
      typingStopTimerRef.current = setTimeout(stopTyping, TYPING_STOP_DELAY_MS)
    } else {
      stopTyping()
    }
  }

  // Own typing signal must not outlive this panel (tab switch, submit, unmount).
  useEffect(() => () => stopTyping(), [])

  // Auto-scroll to the newest message.
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight
  }, [messages.length])

  // Mark every message from "the other side" as seen once it shows up here —
  // this panel being mounted/rendered IS "the recruiter/candidate looked at
  // the chat", so there is no separate visibility check to wire up.
  useEffect(() => {
    messages.forEach((m) => {
      if (m.sender_role === ownRole) return
      if (markedRef.current.has(m.id)) return
      markedRef.current.add(m.id)
      markSeen(m.id).catch(() => {
        // Best-effort — a failed seen-receipt shouldn't break the chat UI;
        // un-mark so a later render/reconnect can retry.
        markedRef.current.delete(m.id)
      })
    })
  }, [messages, ownRole, markSeen])

  const handleSend = () => {
    const text = draft.trim()
    if (!text) return
    onSend(text)
    setDraft('')
    stopTyping()
  }

  // Every scroll/overflow decision lives on the message list alone
  // (`flex-1 min-h-0 overflow-y-auto`); the outer column and the input row
  // carry no height of their own, so this component cooperates with ANY
  // flexible parent height — a short one, a tall one, or one being resized
  // live by a SplitPane divider — without the input ever being pushed off
  // the bottom or the list refusing to grow into freed space.
  return (
    <div className={`flex flex-col h-full min-h-0 ${className || ''}`}>
      {!hideHeader && (
        <p className={`flex-shrink-0 text-xs font-semibold mb-2 flex items-center gap-1.5 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
          <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" /> Chat
        </p>
      )}
      <div
        ref={listRef}
        className={`flex-1 overflow-y-auto space-y-2 rounded-md p-2.5 ${fillHeight ? 'min-h-0' : 'min-h-[140px] max-h-64'} ${
          dark ? 'bg-white/[0.03] border border-white/10' : 'bg-gray-50 border border-gray-200'
        }`}
      >
        {messages.length === 0 ? (
          <p className={`text-xs text-center py-4 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>No messages yet.</p>
        ) : (
          messages.map((m) => {
            const isOwn = m.sender_role === ownRole
            // A message just received over the live websocket (or an
            // optimistic local echo of one just sent) may not carry a
            // seen_by array yet — only REST-loaded history is guaranteed to
            // have it populated. Never trust a live socket payload to match
            // its REST-shape type exactly.
            const seen = (m.seen_by || []).length > 0
            return (
              <div key={m.id} className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
                <div
                  className={`max-w-[85%] rounded-md px-2.5 py-1.5 text-xs ${
                    isOwn
                      ? 'bg-purple-600 text-white'
                      : dark ? 'bg-white/10 border border-white/10 text-gray-100' : 'bg-white border border-gray-200 text-gray-800'
                  }`}
                >
                  {m.text}
                </div>
                <span className={`text-[10px] mt-0.5 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
                  {formatTime(m.sent_at)}
                  {isOwn && seen ? ' · Seen' : ''}
                </span>
              </div>
            )
          })
        )}
      </div>
      {otherTyping && (
        <p className={`flex-shrink-0 text-[11px] italic mt-1 flex items-center gap-1 ${dark ? 'text-gray-500' : 'text-gray-400'}`}>
          <span className="inline-flex gap-0.5">
            <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.2s]" />
            <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce [animation-delay:-0.1s]" />
            <span className="w-1 h-1 rounded-full bg-gray-400 animate-bounce" />
          </span>
          {ownRole === 'recruiter' ? 'Candidate' : 'Interviewer'} is typing…
        </p>
      )}
      <div className="flex-shrink-0 flex items-center gap-2 mt-2">
        <input
          value={draft}
          onChange={(e) => handleDraftChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              handleSend()
            }
          }}
          placeholder="Type a message…"
          className={`flex-1 min-w-0 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 ${
            dark
              ? 'bg-white/5 border border-white/15 text-gray-100 placeholder-gray-500 focus:border-purple-400 focus:ring-purple-500/20'
              : 'border border-gray-300 focus:border-blue-400 focus:ring-blue-100'
          }`}
        />
        <button
          onClick={handleSend}
          disabled={!draft.trim()}
          className="flex-shrink-0 inline-flex items-center justify-center p-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
        >
          <PaperAirplaneIcon className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}
